/**
 * t719u: editing an announcement from a phone, and by someone other than its author ("Edit and delete any announcement").
 * Shared by the web composer (desktop and phone web) and the iPhone app. The API is the authority; these only decide what to show.
 */
import { canWith, type MyPermissions } from '../permissions';
import { docFromText, type RichDoc, type RichNode } from './doc';

type Aud = { location_ids?: readonly string[] | null; team_ids?: readonly string[] | null; position_ids?: readonly string[] | null; user_ids?: readonly string[] | null };

/**
 * May I change who sees this post? "Publish announcements" must cover its whole audience: every building and team, and
 * company-wide for an empty audience, positions on their own, or named people. Otherwise an edit keeps the audience.
 */
export function audienceEditable(mine: Pick<MyPermissions, 'workspace' | 'scoped'> | null | undefined, a: Aud): boolean {
  const perm = 'comms.announcement.publish';
  if (canWith(mine, perm)) return true;
  if (a.user_ids?.length) return false;
  const locs = a.location_ids ?? [], teams = a.team_ids ?? [];
  const refs = [...locs.map(id => `location:${id}`), ...teams.map(id => `team:${id}`)];
  if (!refs.length) return false;
  if ((a.position_ids?.length ?? 0) > 0 && !locs.length) return false;
  return refs.every(r => canWith(mine, perm, [r]));
}

const plainAttrs = (n: RichNode) => !n.attrs || Object.values(n.attrs).every(v => v == null);

/**
 * The body as plain text when it is only unformatted paragraphs (what the iPhone composer writes), so it can be edited
 * in a plain text box without losing anything; null when it has formatting, links, lists, images and so on.
 */
export function plainTextOf(doc: RichNode | null | undefined): string | null {
  if (!doc || doc.type !== 'doc') return null;
  const paras = doc.content ?? [];
  const out: string[] = [];
  for (const p of paras) {
    if (p.type !== 'paragraph' || !plainAttrs(p)) return null;
    let s = '';
    for (const t of p.content ?? []) {
      if (t.type !== 'text' || typeof t.text !== 'string' || (t.marks?.length ?? 0) > 0 || !plainAttrs(t)) return null;
      s += t.text;
    }
    out.push(s);
  }
  const text = out.join('\n\n');
  // Round trip: only offer plain editing when saving the same text gives back the same post.
  const back = docFromText(text).content ?? [];
  const same = back.length === paras.length && back.every((p, i) => (p.content?.[0]?.text ?? '') === out[i]);
  return same ? text : null;
}

/** The body to save from a plain text box: the new text, or the original rich body untouched when it was not plain. */
export function bodyForSave(original: RichDoc | null | undefined, text: string | null): RichDoc {
  if (text == null && original) return original;
  return docFromText(text ?? '');
}

/** t720u: what an old link or notification to a deleted announcement says (the API's 404 message; the pages show it). */
export const ANNOUNCEMENT_DELETED = 'This announcement has been deleted';
/** t720u: the API said the announcement was deleted (an ApiError's server text or body). */
export function isAnnouncementDeleted(e: unknown): boolean {
  const x = e as { detail?: unknown; body?: { message?: unknown } | null } | null;
  return Boolean(x) && (x!.detail === ANNOUNCEMENT_DELETED || x!.body?.message === ANNOUNCEMENT_DELETED);
}
